import { colors } from '@mui/material';
import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 20
  },
  heading: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20
  },
  line: {
    height: 0.6,
    backgroundColor: 'black',
    marginVertical: 10,
    marginTop: '40px'
  },
  line2: {
    height: 0.4,
    backgroundColor: 'black',
    marginVertical: 10
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0,
    border: '1px Solid black',
    padding: '10px'
  },
  contentContainer2: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 0,
    marginBottom: 0
  },
  column: {
    flex: 1,
    paddingHorizontal: 10
  },
  row: {
    flexDirection: 'row',
    marginBottom: 0
  },
  text: {
    fontSize: 9,
    display: 'inline-block'
  },
  textBold: {
    fontSize: 9,
    fontWeight: 'bold'
  },
  textUnderlined: {
    textDecoration: 'underline'
  },

  columnHeader: {
    width: '15%',
    backgroundColor: '#1976D2',
    color: 'white',
    padding: 5,
    textAlign: 'center'
  },
  columnContent: {
    width: '15%',
    padding: 6,
    textAlign: 'center',
    alignItems: 'center'
  },
  columnContent1: {
    width: '6%',
    padding: 6,
    textAlign: 'center',
    alignItems: 'center'
  },
  totalRow: {
    flexDirection: 'row',
    marginTop: 5,
    marginBottom: 10,
    backgroundColor: '#f0f0f0',
    borderTopWidth: 1,
    borderTopColor: '#000',
    padding: 5
  },
  totalText: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'right',
    paddingRight: 10
  },
  totalValue: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  grandTotalRow: {
    flexDirection: 'row',
    marginTop: 10,
    marginBottom: 5,
    backgroundColor: '#d3d3d3',
    borderTopWidth: 2,
    borderTopColor: '#000',
    padding: 8
  },
  sectionHeader: {
    fontSize: '10px',
    marginTop: 10,
    marginBottom: 5,
    fontWeight: 'bold'
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 5,
    marginBottom: 5,
    paddingRight: 20
  }
});

const fullScreenStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'white'
  }
};
const COLORS = ['#e0e0e0', '#ffffff'];

const Page = ({ style, children, ...props }) => (
  <PDFPage style={{ ...styles.page, ...style }} {...props}>
    {children}
  </PDFPage>
);

const View = ({ style, children, ...props }) => (
  <PDFView style={{ ...style }} {...props}>
    {children}
  </PDFView>
);

const Text = ({ style, content, ...props }) => (
  <PDFText style={{ ...styles.text, ...style }} {...props}>
    {content}
  </PDFText>
);

export const DoctorLedgerReportPdf = ({ data }) => {
  console.log(data);
  const user = JSON.parse(localStorage.getItem('user'));

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const groupByDate = (data) => {
    return data.reduce((groups, item) => {
      const date = formatDate(item?.date);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
      return groups;
    }, {});
  };

  const groupByService = (data) => {
    return data.reduce((acc, curr) => {
      const serviceName = curr.serviceDetail?.service_name || 'Unknown';
      if (!acc[serviceName]) {
        acc[serviceName] = [];
      }
      acc[serviceName].push(curr);
      return acc;
    }, {});
  };

  // Format the grouped data to render in your PDF
  const groupedData = groupByDate(data?.ipdBillingData?.data);
  const opdGroupData = groupByDate(data?.opdData?.data);
  const opdBillingGroupData = groupByDate(data?.opdBillingData?.data);

  // Calculate totals for OPD Consultancy
  const calculateOPDTotal = () => {
    let total = 0;
    Object.keys(opdGroupData).forEach(groupDate => {
      Object.keys(groupByService(opdGroupData[groupDate])).forEach(serviceName => {
        groupByService(opdGroupData[groupDate])[serviceName].forEach(data => {
          total += parseFloat(data.reg) || 0;
        });
      });
    });
    return total;
  };

  // Calculate totals for OPD Billing
  const calculateOPDBillingTotal = () => {
    let total = 0;
    Object.keys(opdBillingGroupData).forEach(groupDate => {
      Object.keys(groupByService(opdBillingGroupData[groupDate])).forEach(serviceName => {
        groupByService(opdBillingGroupData[groupDate])[serviceName].forEach(data => {
          total += parseFloat(data.service_items?.amount) || 0;
        });
      });
    });
    return total;
  };

  // Calculate totals for IPD Billing
  const calculateIPDTotal = () => {
    let total = 0;
    Object.keys(groupedData).forEach(groupDate => {
      Object.keys(groupByService(groupedData[groupDate])).forEach(serviceName => {
        groupByService(groupedData[groupDate])[serviceName].forEach(data => {
          total += parseFloat(data.service_items?.amount) || 0;
        });
      });
    });
    return total;
  };

  const opdTotal = calculateOPDTotal();
  const opdBillingTotal = calculateOPDBillingTotal();
  const ipdTotal = calculateIPDTotal();
  const grandTotal = opdTotal + opdBillingTotal + ipdTotal;

  return (
    <div style={fullScreenStyles.container}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Doctor Ledger Detail Report">
          <Page style={styles.page} size="A4" orientation="landscape">
            <PDFImage
              src="/logo_Hospital_m.jpeg"
              style={{ width: '100%', height: '80px', marginBottom: 1, marginRight: '10%' }}
            />
            <Text 
              style={{ ...styles.text, width: '100%', fontSize: "15px", textAlign: "center", fontWeight: 'bold' }} 
              content={data?.opdData?.data[0]?.consultant?.name} 
            />
            
            {/* Header Row */}
            <View style={styles.row}>
              <Text style={{ ...styles.columnHeader, width: '80px' }} content="Sr No" />
              <Text style={{ ...styles.columnHeader, width: '250px' }} content="Patient Name" />
              <Text style={styles.columnHeader} content="Uhid No." />
              <Text style={styles.columnHeader} content="Adm No" />
              <Text style={{ ...styles.columnHeader, width: '30%' }} content="Address" />
              <Text style={styles.columnHeader} content="Mobile" />
              <Text style={styles.columnHeader} content="Service Type" />
              <Text style={styles.columnHeader} content="Paymode" />
              <Text style={{ ...styles.columnHeader }} content="Panel" />
              <Text style={styles.columnHeader} content="Bill Amt." />
            </View>

            {/* OPD Consultancy Section */}
            <View style={styles.row}>
              <Text style={{ fontSize: '10px', fontWeight: 'bold', color: 'red', marginTop: 10 }} content="OPD (Consultancy)" />
            </View>

            {Object.keys(opdGroupData).map((groupDate) => {
              let groupTotal = 0;
              
              return (
                <View key={groupDate} style={{ marginBottom: 20 }}>
                  <Text style={{ ...styles.header, fontSize: '10px', fontWeight: 'bold' }} content={groupDate} />

                  {/* Group by service name */}
                  {Object.keys(groupByService(opdGroupData[groupDate])).map((serviceName) => (
                    <View key={serviceName}>
                      <Text style={{ fontSize: '10px', marginTop: '10px', fontStyle: 'italic' }} content={"Consultancy"} />
                      {groupByService(opdGroupData[groupDate])[serviceName].map((data, index) => {
                        groupTotal += parseFloat(data.reg) || 0;
                        
                        return (
                          <View key={index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
                            <Text style={{ ...styles.columnContent, width: '80px' }} content={index + 1} />
                            <Text
                              style={{ ...styles.columnContent, width: '250px', textAlign: 'left' }}
                              content={`${data.name_suffix} ${data.first_name} ${data.last_name}`}
                            />
                            <Text style={styles.columnContent} content={data.uhid} />
                            <Text style={styles.columnContent} content={data?.patient?.adm_no_date} />
                            <Text style={{ ...styles.columnContent, width: '30%', textAlign: 'start' }} content={data?.address?.address} />
                            <Text style={styles.columnContent} content={data?.address?.mobile} />
                            <Text style={styles.columnContent} content={""} />
                            <Text style={styles.columnContent} content={data.payment_mode} />
                            <Text style={styles.columnContent} content={data.sponsor.name} />
                            <Text style={{ ...styles.columnContent, textAlign: 'right' }} content={data.reg} />
                          </View>
                        );
                      })}
                    </View>
                  ))}
                  
                  {/* Daily total for this date */}
                  <View style={styles.summaryRow}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }} content={`Daily Total: ${groupTotal.toFixed(2)}`} />
                  </View>
                </View>
              );
            })}

            {/* OPD Consultancy Total */}
            <View style={styles.totalRow}>
              <Text style={{ ...styles.totalText, width: '90%' }} content="OPD Consultancy Total:" />
              <Text style={{ ...styles.totalValue, width: '10%' }} content={opdTotal.toFixed(2)} />
            </View>

            {/* OPD Billing Section */}
            <View style={styles.row}>
              <Text style={{ fontSize: '10px', fontWeight: 'bold', color: 'red', marginTop: 10 }} content="OPD BILLING" />
            </View>

            {Object.keys(opdBillingGroupData).map((groupDate) => {
              let groupTotal = 0;
              
              return (
                <View key={groupDate} style={{ marginBottom: 20 }}>
                  <Text style={{ ...styles.header, fontSize: '10px', fontWeight: 'bold' }} content={groupDate} />

                  {/* Group by service name */}
                  {Object.keys(groupByService(opdBillingGroupData[groupDate])).map((serviceName) => (
                    <View key={serviceName}>
                      <Text style={{ fontSize: '10px', marginTop: '10px', fontStyle: 'italic' }} content={serviceName} />
                      {groupByService(opdBillingGroupData[groupDate])[serviceName].map((data, index) => {
                        groupTotal += parseFloat(data.service_items?.amount) || 0;
                        
                        return (
                          <View key={index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
                            <Text style={{ ...styles.columnContent, width: '80px' }} content={index + 1} />
                            <Text
                              style={{ ...styles.columnContent, width: '250px', textAlign: 'left' }}
                              content={`${data.patient.name_suffix} ${data.patient.first_name} ${data.patient.last_name}`}
                            />
                            <Text style={styles.columnContent} content={data.uhid} />
                            <Text style={styles.columnContent} content={data.patient.adm_no_date} />
                            <Text style={{ ...styles.columnContent, width: '30%', textAlign: 'start' }} content={data?.patient?.address?.address} />
                            <Text style={styles.columnContent} content={data?.patient?.address?.mobile} />
                            <Text style={styles.columnContent} content={data?.serviceDetail?.type} />
                            <Text style={styles.columnContent} content={data.pay_mode} />
                            <Text style={styles.columnContent} content={data.patient.sponsor.name} />
                            <Text
                              style={{ ...styles.columnContent, textAlign: 'right' }}
                              content={data.service_items?.amount}
                            />
                          </View>
                        );
                      })}
                    </View>
                  ))}
                  
                  {/* Daily total for this date */}
                  <View style={styles.summaryRow}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }} content={`Daily Total: ${groupTotal.toFixed(2)}`} />
                  </View>
                </View>
              );
            })}

            {/* OPD Billing Total */}
            <View style={styles.totalRow}>
              <Text style={{ ...styles.totalText, width: '90%' }} content="OPD Billing Total:" />
              <Text style={{ ...styles.totalValue, width: '10%' }} content={opdBillingTotal.toFixed(2)} />
            </View>

            {/* IPD Billing Section */}
            <View style={styles.row}>
              <Text style={{ fontSize: '10px', fontWeight: 'bold', color: 'red', marginTop: 10 }} content="IPD BILLING" />
            </View>

            {Object.keys(groupedData).map((groupDate) => {
              let groupTotal = 0;
              
              return (
                <View key={groupDate} style={{ marginBottom: 20 }}>
                  <Text style={{ ...styles.header, fontSize: '10px', fontWeight: 'bold' }} content={groupDate} />

                  {/* Group by service name */}
                  {Object.keys(groupByService(groupedData[groupDate])).map((serviceName) => (
                    <View key={serviceName}>
                      <Text style={{ fontSize: '10px', marginTop: '10px', fontStyle: 'italic' }} content={serviceName} />
                      {groupByService(groupedData[groupDate])[serviceName].map((data, index) => {
                        groupTotal += parseFloat(data.service_items?.amount) || 0;
                        
                        return (
                          <View key={index} style={{ ...styles.row, backgroundColor: COLORS[index % COLORS.length] }}>
                            <Text style={{ ...styles.columnContent, width: '80px' }} content={index + 1} />
                            <Text
                              style={{ ...styles.columnContent, width: '250px', textAlign: 'left' }}
                              content={`${data.patient.name_suffix} ${data.patient.first_name} ${data.patient.last_name}`}
                            />
                            <Text style={styles.columnContent} content={data?.patient?.uhid} />
                            <Text style={styles.columnContent} content={data.ipd_no} />
                            <Text style={{ ...styles.columnContent, width: '30%', textAlign: 'start' }} content={data?.patient?.address?.address} />
                            <Text style={styles.columnContent} content={data?.patient?.address?.mobile} />
                            <Text style={styles.columnContent} content={data?.serviceDetail?.type} />
                            <Text style={styles.columnContent} content={data.pay_mode} />
                            <Text style={styles.columnContent} content={data.patient.sponsor.name} />
                            <Text
                              style={{ ...styles.columnContent, textAlign: 'right' }}
                              content={data.service_items?.amount}
                            />
                          </View>
                        );
                      })}
                    </View>
                  ))}
                  
                  {/* Daily total for this date */}
                  <View style={styles.summaryRow}>
                    <Text style={{ fontSize: 9, fontWeight: 'bold' }} content={`Daily Total: ${groupTotal.toFixed(2)}`} />
                  </View>
                </View>
              );
            })}

            {/* IPD Billing Total */}
            <View style={styles.totalRow}>
              <Text style={{ ...styles.totalText, width: '90%' }} content="IPD Billing Total:" />
              <Text style={{ ...styles.totalValue, width: '10%' }} content={ipdTotal.toFixed(2)} />
            </View>

            {/* Grand Total */}
            <View style={styles.grandTotalRow}>
              <Text style={{ ...styles.totalText, width: '90%', fontSize: 11 }} content="GRAND TOTAL:" />
              <Text style={{ ...styles.totalValue, width: '10%', fontSize: 11 }} content={grandTotal.toFixed(2)} />
            </View>

          </Page>
        </Document>
      </PDFViewer>
    </div>
  );
};